import { RefreshCwIcon, SaveIcon, SparklesIcon, Trash2Icon, XIcon } from "lucide-react";
import { useEffectEvent, useState } from "react";
import type { CmmdProjectTaskDraftProposal } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { DialogFooter, DialogHeader, DialogPanel, DialogTitle } from "~/components/ui/dialog";
import type { ProjectLeadTaskDraftController } from "./useProjectLeadTaskDraft";
import {
  addSubtask,
  MAX_TASKS_PER_PROPOSAL,
  moveTask,
  removeTaskBranch,
  splitTask,
} from "./taskDraftModel";
import { ProjectLeadSuggestions } from "./ProjectLeadSuggestions";
import { ProjectLeadTaskInspector } from "./ProjectLeadTaskInspector";
import { ProjectLeadTaskTree } from "./ProjectLeadTaskTree";
import { ProjectLeadWorkbenchStatus } from "./ProjectLeadWorkbenchStatus";
import { ProjectLeadStaleReloadDialog } from "./ProjectLeadStaleReloadDialog";
import { ProjectLeadCommittedState } from "./ProjectLeadCommittedState";
import { useProtectedProjectLeadRoot } from "./useProtectedProjectLeadRoot";

export interface ProjectLeadTaskWorkbenchProps {
  readonly projectTitle: string;
  readonly controller: ProjectLeadTaskDraftController;
  readonly onClose: () => void;
}

function draftProposalState(controller: ProjectLeadTaskDraftController) {
  return {
    draftId: controller.draft?.id ?? -1,
    tasks: controller.proposal?.tasks ?? [],
  };
}

export function ProjectLeadTaskWorkbench(props: ProjectLeadTaskWorkbenchProps) {
  const [confirming, setConfirming] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [confirmingStaleReload, setConfirmingStaleReload] = useState(false);
  const closeLatest = useEffectEvent(props.onClose);
  const requestClose = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.setTimeout(closeLatest, 0);
  };
  const draft = props.controller.draft;
  const proposal = props.controller.proposal;
  const rootState = draftProposalState(props.controller);
  const protectedRootRef = useProtectedProjectLeadRoot(rootState.draftId, rootState.tasks);
  if (draft === null) return null;
  if (draft.status === "committed") {
    return (
      <ProjectLeadCommittedState
        taskIds={props.controller.committedTaskIds}
        onClose={requestClose}
      />
    );
  }
  if (proposal === null) return null;
  const selectedTask =
    proposal.tasks.find((task) => task.localRef === props.controller.selectedRef) ??
    proposal.tasks[0] ??
    null;
  const atTaskLimit = proposal.tasks.length >= MAX_TASKS_PER_PROPOSAL;
  const edit = (next: CmmdProjectTaskDraftProposal) => props.controller.edit(next);
  const add = (parentRef: string) => {
    const localRef = `task-${crypto.randomUUID()}`;
    edit(addSubtask(proposal, parentRef, localRef));
    props.controller.select(localRef);
  };
  const split = (localRef: string) => {
    const newLocalRef = `task-${crypto.randomUUID()}`;
    edit(splitTask(proposal, localRef, newLocalRef));
    props.controller.select(newLocalRef);
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <DialogTitle className="text-base">New task tree</DialogTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {props.projectTitle} · Review with Project Lead before anything is created
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <SparklesIcon className="size-4 text-primary" aria-hidden />
            Draft v{draft.version}
            {props.controller.dirty ? <span className="text-primary">Unsaved edits</span> : null}
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              aria-label="Close"
              onClick={requestClose}
            >
              <XIcon aria-hidden />
            </Button>
          </div>
        </div>
      </DialogHeader>

      <>
        <DialogPanel
          data-project-lead-task-layout
          className="grid min-h-0 flex-1 grid-rows-[minmax(13rem,34%)_minmax(0,1fr)] overflow-hidden p-0 md:grid-cols-[14rem_minmax(0,1fr)] md:grid-rows-1 xl:grid-cols-[15rem_minmax(0,1fr)]"
        >
          <ProjectLeadTaskTree
            tasks={proposal.tasks}
            selectedRef={selectedTask?.localRef ?? null}
            disabled={props.controller.busy}
            atTaskLimit={atTaskLimit}
            protectedRootRef={protectedRootRef}
            onSelect={props.controller.select}
            onAddSubtask={add}
            onMove={(localRef, direction) => edit(moveTask(proposal, localRef, direction))}
            onSplit={split}
            onRemove={(localRef) => {
              edit(removeTaskBranch(proposal, localRef));
              const rootRef = proposal.tasks[0]?.localRef;
              if (rootRef !== undefined) props.controller.select(rootRef);
            }}
          />
          {atTaskLimit ? (
            <p role="alert" className="sr-only">
              Task trees are limited to 50 tasks. Remove a task before adding or splitting another.
            </p>
          ) : null}
          {selectedTask ? (
            <ProjectLeadTaskInspector
              proposal={proposal}
              task={selectedTask}
              disabled={props.controller.busy}
              onChange={edit}
            />
          ) : (
            <p className="m-auto text-sm text-muted-foreground">Add a task to continue.</p>
          )}
        </DialogPanel>
        <ProjectLeadSuggestions
          suggestions={draft.suggestions}
          disabled={props.controller.busy}
          onResolve={(suggestionId, resolution) => {
            void props.controller.resolveSuggestion(suggestionId, resolution);
          }}
        />
        <ProjectLeadWorkbenchStatus controller={props.controller} />
        <DialogFooter
          data-project-lead-companion-safe-boundary
          className="shrink-0 flex-nowrap justify-start gap-2 overflow-x-auto border-t border-border px-3 py-2 md:justify-between md:px-4"
          variant="bare"
        >
          <div className="flex shrink-0 gap-[8px]">
            <Button
              type="button"
              variant="ghost"
              className={`${RESPONSIVE_TEXT_BUTTON_CLASS} text-destructive hover:text-destructive`}
              disabled={props.controller.busy}
              onClick={() => setConfirmingDiscard(true)}
            >
              <Trash2Icon aria-hidden /> Discard draft
            </Button>
            <Button
              type="button"
              variant="ghost"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              onClick={requestClose}
            >
              Save draft and close
            </Button>
            <Button
              type="button"
              variant="outline"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={props.controller.busy || !props.controller.dirty}
              onClick={() => void props.controller.save()}
            >
              <SaveIcon aria-hidden /> Save draft
            </Button>
            <Button
              type="button"
              variant="outline"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={
                props.controller.busy || (props.controller.dirty && !props.controller.stale)
              }
              title={
                props.controller.dirty && !props.controller.stale
                  ? "Save or discard local edits before reloading."
                  : undefined
              }
              onClick={() => {
                if (props.controller.dirty) setConfirmingStaleReload(true);
                else void props.controller.reload();
              }}
            >
              <RefreshCwIcon aria-hidden /> Reload
            </Button>
          </div>
          <div className="flex shrink-0 gap-[8px]">
            <Button
              type="button"
              variant="outline"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={props.controller.busy || proposal.tasks.length === 0}
              onClick={() => void props.controller.validate()}
            >
              Check task tree
            </Button>
            <Button
              type="button"
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={
                props.controller.busy ||
                proposal.tasks.length === 0 ||
                props.controller.preview === null
              }
              onClick={() => setConfirming(true)}
            >
              Create task tree
            </Button>
          </div>
        </DialogFooter>
        <AlertDialog open={confirming} onOpenChange={setConfirming}>
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>Create {proposal.tasks.length} tasks?</AlertDialogTitle>
              <AlertDialogDescription>
                This atomically creates the reviewed parent task, subtasks, and dependencies on the
                project board. Nothing has been created yet.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>
                Keep reviewing
              </AlertDialogClose>
              <Button
                disabled={props.controller.busy}
                onClick={() => {
                  setConfirming(false);
                  void props.controller.commit();
                }}
              >
                Confirm and create task tree
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
        <AlertDialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard this task draft?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently abandons the saved Project Lead draft and all local edits. No board
                tasks will be created. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>Keep draft</AlertDialogClose>
              <Button
                variant="destructive"
                disabled={props.controller.busy}
                onClick={() => {
                  setConfirmingDiscard(false);
                  void props.controller.discard();
                }}
              >
                Discard draft permanently
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
        <ProjectLeadStaleReloadDialog
          open={confirmingStaleReload}
          onOpenChange={setConfirmingStaleReload}
          onConfirm={() => {
            setConfirmingStaleReload(false);
            void props.controller.reload();
          }}
        />
      </>
    </div>
  );
}
